/**
 * The HTML the build writes for each public and docs page (scripts/prerender.mjs).
 *
 * Each page is rendered once per locale into its own file, so crawlers that do
 * not run JavaScript still get the content, and every language version names
 * the others (hreflang) and itself (canonical). The same file carries the tags
 * a link preview is built from.
 */

import { LOCALES, localizePath, splitLocalePath, type Locale } from './locale-path';
import { SINGLE_LOCALE_PAGES, sitePaths } from './site-routes';
import { SITE_TITLE } from './document-meta';

export const SITE_ORIGIN = 'https://sokuji.kizuna.ai';

const RTL: ReadonlySet<Locale> = new Set<Locale>(['ar']);

export interface PageVariant {
  locale: Locale;
  url: string;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The URL of a page in each locale it exists in, English first. */
export function pageVariants(page: string): PageVariant[] {
  if (SINGLE_LOCALE_PAGES.has(page)) return [{ locale: 'en', url: page }];
  return LOCALES.map((locale) => ({ locale, url: localizePath(page, locale) }));
}

export interface HeadLink {
  rel: 'canonical' | 'alternate';
  hreflang?: string;
  href: string;
}

const SITE_PAGES: ReadonlySet<string> = new Set(sitePaths());

/**
 * canonical and hreflang links for a URL path: the prerender writes them into
 * each file, and the app rewrites them after every client-side navigation.
 * Empty for anything that is not a site page (app routes).
 */
export function headLinks(pathname: string): HeadLink[] {
  const url = pathname.replace(/\/+$/, '') || '/';
  const { path } = splitLocalePath(url);
  if (!SITE_PAGES.has(path)) return [];
  const variants = pageVariants(path);
  const links: HeadLink[] = [{ rel: 'canonical', href: SITE_ORIGIN + url }];
  if (variants.length > 1) {
    for (const v of variants) links.push({ rel: 'alternate', hreflang: v.locale, href: SITE_ORIGIN + v.url });
    links.push({ rel: 'alternate', hreflang: 'x-default', href: SITE_ORIGIN + variants[0].url });
  }
  return links;
}

/** The picture every page shows in a link preview (web/public/og-image.png). */
export const SHARE_IMAGE = { url: `${SITE_ORIGIN}/og-image.png`, width: 1200, height: 630 } as const;

/**
 * og:locale wants language_TERRITORY; pt is written Brazilian, zh Simplified.
 * ar_AR and es_LA are not Argentina and Laos: they are Facebook's documented
 * umbrella locales for Arabic and Spanish, which fit catalogs written for no
 * one country.
 */
const OG_LOCALE: Record<Locale, string> = {
  en: 'en_US',
  zh: 'zh_CN',
  ja: 'ja_JP',
  ko: 'ko_KR',
  es: 'es_LA',
  fr: 'fr_FR',
  ar: 'ar_AR',
  pt: 'pt_BR',
  ru: 'ru_RU',
  de: 'de_DE',
  uk: 'uk_UA',
  it: 'it_IT',
};

/**
 * Open Graph and X card tags, which link previews (X, Facebook, LINE, Slack,
 * Discord) are built from. Their crawlers do not run JavaScript, so only the
 * prerendered file matters and nothing rewrites these after load.
 */
function shareTags(url: string, title: string, description?: string): string[] {
  const meta = (attr: 'property' | 'name', key: string, value: string | number) =>
    `<meta ${attr}="${key}" content="${escapeHtml(String(value))}" />`;
  return [
    meta('property', 'og:type', 'website'),
    meta('property', 'og:site_name', 'Sokuji'),
    meta('property', 'og:url', SITE_ORIGIN + url),
    meta('property', 'og:title', title),
    ...(description ? [meta('property', 'og:description', description)] : []),
    meta('property', 'og:image', SHARE_IMAGE.url),
    meta('property', 'og:image:width', SHARE_IMAGE.width),
    meta('property', 'og:image:height', SHARE_IMAGE.height),
    meta('property', 'og:locale', OG_LOCALE[splitLocalePath(url).locale ?? 'en']),
    meta('name', 'twitter:card', 'summary_large_image'),
  ];
}

/** The <head> additions for one prerendered page. */
export function headTags(opts: { url: string; variants: PageVariant[]; title?: string; description?: string }): string {
  const tags = [`<meta name="prerendered-path" content="${escapeHtml(opts.url)}" />`];
  if (opts.title) tags.push(`<title>${escapeHtml(opts.title)}</title>`);
  if (opts.description) tags.push(`<meta name="description" content="${escapeHtml(opts.description)}" />`);
  for (const l of headLinks(opts.url)) {
    const lang = l.hreflang ? ` hreflang="${l.hreflang}"` : '';
    tags.push(`<link rel="${l.rel}"${lang} href="${escapeHtml(l.href)}" />`);
  }
  tags.push(...shareTags(opts.url, opts.title ?? SITE_TITLE, opts.description));
  return tags.join('\n    ');
}

/** Puts a rendered page into the built index.html. */
export function fillTemplate(template: string, page: { locale: Locale; head: string; html: string }): string {
  let out = template.replace(/<html lang="[^"]*"[^>]*>/, `<html lang="${page.locale}" dir="${RTL.has(page.locale) ? 'rtl' : 'ltr'}">`);
  if (page.head.includes('<title>')) out = out.replace(/\s*<title>[\s\S]*?<\/title>/, '');
  // After <meta charset> (browsers only look for it in the first 1024 bytes)
  // and before index.html's inline script, which reads prerendered-path.
  out = out.replace(/<meta charset="[^"]*" \/>\n?/, (charset) => `${charset}    ${page.head}\n`);
  return out.replace('<div id="root"></div>', `<div id="root">${page.html}</div>`);
}

/**
 * The file a URL is written to. "/docs" becomes docs.html, not docs/index.html:
 * the asset server answers "/docs" from docs.html directly, while a directory
 * index would first redirect to "/docs/".
 */
export function outputFile(url: string): string {
  return url === '/' ? 'index.html' : `${url.slice(1)}.html`;
}

/** sitemap.xml with every language version of every page, each naming its alternates. */
export function buildSitemap(pages: string[]): string {
  const entries = pages.flatMap((page) => {
    const variants = pageVariants(page);
    return variants.map((v) => {
      const alternates =
        variants.length > 1
          ? [...variants.map((a) => [a.locale, a.url]), ['x-default', variants[0].url]]
              .map(([lang, url]) => `\n    <xhtml:link rel="alternate" hreflang="${lang}" href="${SITE_ORIGIN}${url}" />`)
              .join('')
          : '';
      return `  <url>\n    <loc>${SITE_ORIGIN}${v.url}</loc>${alternates}\n  </url>`;
    });
  });
  return (
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">\n' +
    entries.join('\n') +
    '\n</urlset>\n'
  );
}
